import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CameraScanner, MediaInput } from "@/components/erp/prod/ProdInputs";
import { Modal, Btn, Input } from "@/components/erp/ui";
import { ScanLine, Square, Utensils, Moon, HandHeart, Bath, Pause, Play } from "lucide-react";

const BICON = { LUNCH: Utensils, DINNER: Moon, PRAY: HandHeart, TOILET: Bath, PAUSE: Pause };

const hms = (s) => `${Math.floor(s / 3600)}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
const err = (e) => toast.error(e?.response?.data?.detail || "Gagal");
export const procLabel = (procs, k) => procs.find((p) => p.key === k)?.label || k;

const Active = ({ mine, procs, onStop, d, load }) => {
  const [, setT] = useState(0);
  useEffect(() => { const t = setInterval(() => setT((x) => x + 1), 1000); return () => { clearInterval(t); }; }, []);
  return (
    <div className="rounded-2xl bg-emerald-600 text-white p-5 mb-4" data-testid="fin-active">
      <div className="text-sm opacity-90">Sedang dikerjakan</div>
      <div className="text-2xl font-bold">{procLabel(procs, mine.process)} · {mine.wo_code}</div>
      <div className="font-mono text-5xl my-3" data-testid="fin-active-timer">{hms((Date.now() - new Date(mine.started_at)) / 1000)}</div>
      {mine.process === "CURING" && <p className="text-xs bg-white/15 rounded-lg px-2 py-1 mb-3" data-testid="fin-curing-hint">Masa tunggu / observasi — dihitung kerja, tidak boleh main HP atau mengerjakan unit lain.</p>}
      <Breaks d={d} load={load} />
      <button type="button" data-testid="fin-stop" onClick={onStop} className="w-full py-5 rounded-xl bg-white text-emerald-700 text-xl font-bold inline-flex items-center justify-center gap-2 active:scale-95 transition-transform"><Square className="w-6 h-6" /> STOP / SELESAI</button>
    </div>
  );
};

const Breaks = ({ d, load }) => {
  const go = async (k) => { try { await api.post("/finishing/break", { kind: k }); toast.success("Istirahat dimulai — tidak dihitung kerja"); load(); } catch (e) { err(e); } };
  return (
    <div className="grid grid-cols-5 gap-2 mb-3" data-testid="fin-breaks">
      {d.breaks.map((b) => { const I = BICON[b.key] || Pause; return (
        <button key={b.key} type="button" data-testid={`fin-break-${b.key}`} onClick={() => go(b.key)} className="py-3 rounded-xl bg-white/15 hover:bg-white/25 text-xs font-semibold flex flex-col items-center gap-1 transition-colors"><I className="w-4 h-4" />{b.label}</button>
      ); })}
    </div>
  );
};

const OnBreak = ({ d, load }) => {
  const [, setT] = useState(0);
  useEffect(() => { const t = setInterval(() => setT((x) => x + 1), 1000); return () => { clearInterval(t); }; }, []);
  const b = d.my_break; const lab = d.breaks.find((x) => x.key === b.kind)?.label || b.kind;
  const end = async () => {
    try {
      const { data } = await api.post("/finishing/break/end");
      if (data.over_max) toast.error(`Istirahat hari ini ${data.day_break_min} mnt — melebihi batas, lembur hari ini otomatis ditolak`, { duration: 9000 });
      else if (data.over_free) toast.warning(`Istirahat hari ini ${data.day_break_min} mnt — kelebihan dipotong dari jam lembur`, { duration: 7000 });
      else toast.success("Lanjut kerja");
      load();
    } catch (e) { err(e); }
  };
  return (
    <div className="rounded-2xl bg-slate-800 text-white p-5 mb-4" data-testid="fin-on-break">
      <div className="text-sm opacity-80">Sedang istirahat · tidak dihitung kerja</div>
      <div className="text-2xl font-bold">{lab}</div>
      <div className="font-mono text-5xl my-3" data-testid="fin-break-timer">{hms((Date.now() - new Date(b.started_at)) / 1000)}</div>
      <div className="text-xs opacity-80 mb-3" data-testid="fin-break-day">Total istirahat hari ini ±{d.my_day?.break_min || 0} mnt · normal {d.settings.break_free_min} mnt · maks {d.settings.break_max_min} mnt (lebih dari normal dipotong dari lembur, lebih dari maks lembur ditolak)</div>
      <button type="button" data-testid="fin-break-end" onClick={end} className="w-full py-5 rounded-xl bg-emerald-500 text-white text-xl font-bold inline-flex items-center justify-center gap-2 active:scale-95 transition-transform"><Play className="w-6 h-6" /> LANJUT KERJA</button>
    </div>
  );
};

const MyPlan = ({ d, pick }) => (
  <div className="rounded-2xl border border-sky-200 bg-sky-50 p-4 mb-4" data-testid="fin-my-plan">
    <div className="font-semibold text-sm mb-2">Tugas saya hari ini</div>
    {d.my_plan.length ? <div className="grid sm:grid-cols-2 gap-2">{d.my_plan.map((p, i) => (
      <button key={i} type="button" data-testid={`fin-plan-${p.wo_id}`} onClick={() => pick(p.wo_id)} className="text-left rounded-xl bg-white border border-sky-200 px-3 py-2 hover:border-sky-500 transition-colors">
        <b>{p.wo_code}</b> · {p.product}<div className="text-xs text-sky-800">{p.processes.map((k) => procLabel(d.processes, k)).join(", ")}</div>
      </button>
    ))}</div> : <p className="text-xs text-slate-600" data-testid="fin-no-plan">Belum ada pembagian tugas dari Kepala Finishing untuk hari ini — pilih sendiri WO & kegiatan di bawah (jadi referensi tugas yang dikerjakan).</p>}
  </div>
);

const Confirm = ({ c, setC, go }) => (
  <Modal open={!!c} onClose={() => setC(null)} title={c?.step === 2 ? "Konfirmasi sekali lagi" : "Mulai kegiatan?"}
    footer={<><Btn variant="ghost" data-testid="fin-confirm-cancel" onClick={() => setC(null)}>Batal</Btn><Btn data-testid="fin-confirm-yes" onClick={() => (c.step === 1 ? setC({ ...c, step: 2 }) : go(c.key))}>{c?.step === 2 ? "Ya, mulai timer" : "Ya"}</Btn></>}>
    {c && <p className="text-sm" data-testid="fin-confirm-text">{c.step === 1 ? <>Mau memulai <b>{c.label}</b> untuk <b>{c.wo}</b>?</> : <>Timer <b>{c.label}</b> akan berjalan sekarang dan dihitung sebagai jam kerja Anda. Lanjutkan?</>}</p>}
  </Modal>
);

// Panel kerja cepat: scan QR WO atau ketuk WO, lalu ketuk proses besar
export const FinMy = ({ d, wo, setWo, load }) => {
  const [cam, setCam] = useState(false); const [mistake, setMistake] = useState(false); const [ask, setAsk] = useState(false); const [ph, setPh] = useState([]); const [note, setNote] = useState("");
  const [conf, setConf] = useState(null);
  const pick = (id) => { const w = d.wos.find((x) => x.wo_id === id); if (w) setWo(w); };
  const stop = () => { setPh([]); setNote(""); setAsk(true); };
  const doStop = async () => { try { const { data } = await api.post("/finishing/stop", { photos: ph, note }); toast.success(`Selesai · ${Math.round(data.minutes)} menit`); setAsk(false); load(); } catch (e) { err(e); } };
  const start = async (p, src = "TAP") => {
    setConf(null);
    try { const { data } = await api.post(`/finishing/wo/${wo.wo_id}/start`, { process: p, source: src, mistake }); toast.success(`${procLabel(d.processes, p)} mulai${data.stopped_previous ? " (pekerjaan sebelumnya otomatis distop)" : ""}`); setMistake(false); load(); } catch (e) { err(e); }
  };
  const onCode = (txt) => {
    setCam(false);
    const id = (txt.match(/wo=([\w-]+)/) || [])[1] || txt.trim();
    const w = d.wos.find((x) => x.wo_id === id || x.wo_code === id);
    if (!w) { toast.error("WO tidak ada di daftar finishing"); return; }
    if (d.mine && d.mine.wo_id === w.wo_id) { stop(); return; }
    setWo(w);
  };
  return (
    <div data-testid="fin-my">
      {d.my_break && <OnBreak d={d} load={load} />}
      {d.mine && <Active mine={d.mine} procs={d.processes} onStop={stop} d={d} load={load} />}
      {!d.mine && !d.my_break && <MyPlan d={d} pick={pick} />}
      <Confirm c={conf} setC={setConf} go={start} />
      {false && <button type="button" data-testid="fin-scan" onClick={() => setCam(true)} className="w-full py-5 mb-4 rounded-2xl bg-slate-900 text-white text-lg font-bold inline-flex items-center justify-center gap-2 active:scale-95 transition-transform"><ScanLine className="w-6 h-6" /> SCAN QR WO</button>}
      {cam && <CameraScanner onCode={onCode} onClose={() => setCam(false)} />}
      <Modal open={ask} onClose={() => setAsk(false)} title="Foto hasil sebelum STOP" footer={<Btn data-testid="fin-stop-confirm" disabled={!ph.length} onClick={doStop}>STOP & Simpan</Btn>}>
        <MediaInput testid="fin-stop-photo" label="Foto hasil proses (wajib)" max={3} value={ph} onChange={setPh} />
        <div className="mt-2"><Input placeholder="catatan singkat (opsional)" data-testid="fin-stop-note" value={note} onChange={(e) => setNote(e.target.value)} /></div>
      </Modal>
      {!wo && <div className="grid sm:grid-cols-2 gap-3" data-testid="fin-wo-list">
        {d.wos.map((w) => (
          <button key={w.wo_id} type="button" data-testid={`fin-wo-${w.wo_id}`} onClick={() => setWo(w)} className="text-left rounded-2xl border-2 border-slate-200 bg-white p-4 hover:border-slate-900 transition-colors">
            <div className="font-bold">{w.wo_code}</div><div className="text-sm text-slate-600">{w.product} · {w.customer}</div>
            {w.assignees?.length > 0 && <div className="text-[11px] text-slate-400 mt-1">Tim: {w.assignees.join(", ")}</div>}
          </button>
        ))}
        {!d.wos.length && <p className="text-sm text-slate-400">Belum ada WO di tahap finishing.</p>}
      </div>}
      {wo && (
        <div data-testid="fin-proc-panel">
          <div className="flex items-center justify-between mb-2"><div className="font-bold text-lg">{wo.wo_code} · {wo.product}</div><button type="button" className="text-sm underline" data-testid="fin-back" onClick={() => setWo(null)}>ganti WO</button></div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {d.processes.map((p) => (
              <button key={p.key} type="button" data-testid={`fin-proc-${p.key}`} onClick={() => setConf({ key: p.key, label: p.label, wo: wo.wo_code, step: 1 })} className={`py-6 rounded-2xl text-base font-bold active:scale-95 transition-transform ${d.mine?.wo_id === wo.wo_id && d.mine?.process === p.key ? "bg-emerald-600 text-white" : "bg-amber-400 text-slate-900 hover:bg-amber-300"}`}>{p.label}</button>
            ))}
          </div>
          <label className="flex items-center gap-2 text-sm mt-3"><input type="checkbox" data-testid="fin-mistake" checked={mistake} onChange={(e) => setMistake(e.target.checked)} /> Perbaikan kesalahan sendiri (lembur tidak dihitung)</label>
        </div>
      )}
    </div>
  );
};
